import { useCallback, useEffect, useRef, useState } from 'react';
import ApiDatabase from 'server';
import { isCanceledRequest } from 'components/perimeter/perimeterErrors';
import { readNotificationLevels } from 'components/perimeter/memberNotifications';

// A member's notification levels, read once when the contact screen opens.
// status: 'loading' | 'ready' | 'error'; levels is null unless ready.
const useMemberNotifications = (idCompany, idEmployee) => {
  const [state, setState] = useState({ status: 'loading', levels: null });
  // Only the latest read may land: a retry replaces the one before it, and
  // nothing lands once the screen is closed.
  const readId = useRef(0);

  const load = useCallback(() => {
    readId.current += 1;
    const current = readId.current;
    if (!idCompany || !idEmployee) {
      setState({ status: 'error', levels: null });
      return;
    }
    setState({ status: 'loading', levels: null });
    ApiDatabase.getAdminMemberNotifications(
      { idCompany, idEmployee },
      (answer) => {
        if (current !== readId.current) return;
        const levels = readNotificationLevels(answer?.data);
        setState(
          levels ? { status: 'ready', levels } : { status: 'error', levels },
        );
      },
      (err) => {
        if (current !== readId.current || isCanceledRequest(err)) return;
        console.error(err);
        setState({ status: 'error', levels: null });
      },
    );
  }, [idCompany, idEmployee]);

  useEffect(() => {
    load();
    return () => {
      readId.current += 1;
    };
  }, [load]);

  // After a save, the api's answer is the member's state; an unusable one is
  // replaced by a fresh read rather than trusting the draft.
  const setSavedLevels = useCallback(
    (levels) => {
      const read = readNotificationLevels(levels);
      if (!read) {
        load();
        return;
      }
      readId.current += 1;
      setState({ status: 'ready', levels: read });
    },
    [load],
  );

  return { ...state, reload: load, setSavedLevels };
};

export default useMemberNotifications;
